import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Stack, Chip, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, IconButton, Tooltip, Collapse, Switch, FormControlLabel,
  CircularProgress, InputAdornment,
} from '@mui/material';
import {
  Business as TenantsIcon,
  Add as AddIcon,
  Pause as SuspendIcon,
  PlayArrow as ReactivateIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockReset as LockResetIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { platformApi } from '../api/services';

interface Tenant {
  id: string;
  name: string;
  plan: string;
  status: string;
  currentExecutives: number;
  currentEAs: number;
  createdAt: string;
  maxExecutiveSeats: number;
  maxEaSeats: number;
  billingEmail?: string;
  aiPlanningEnabled?: boolean;
  transcriptProcessingEnabled?: boolean;
  imageAnalysisEnabled?: boolean;
  pdfExportEnabled?: boolean;
  webhooksEnabled?: boolean;
  apiAccessEnabled?: boolean;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  domain?: string;
  trialExpiresAt?: string;
  // Legacy field names — keep for backwards compatibility
  executiveCount?: number;
  eaCount?: number;
}


const STATUS_CONFIG: Record<string, { color: 'success' | 'error' | 'warning' | 'default' }> = {
  Active: { color: 'success' },
  Suspended: { color: 'error' },
  PendingSetup: { color: 'warning' },
  Cancelled: { color: 'default' },
  Deleted: { color: 'default' },
};

const getExecCount = (t: Tenant) => t.currentExecutives ?? t.executiveCount ?? 0;
const getEaCount = (t: Tenant) => t.currentEAs ?? t.eaCount ?? 0;

export default function PlatformTenantsPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Create form state
  const [newTenant, setNewTenant] = useState({
    name: '',
    plan: 'Starter',
    maxExecutiveSeats: 1,
    maxEaSeats: 1,
    ownerName: '',
    ownerEmail: '',
    ownerPassword: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit dialog state
  const [editTenant, setEditTenant] = useState<Tenant | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    plan: '',
    maxExecutiveSeats: 1,
    maxEaSeats: 1,
    billingEmail: '',
    status: '',
    aiPlanningEnabled: true,
    transcriptProcessingEnabled: false,
    imageAnalysisEnabled: true,
    pdfExportEnabled: false,
    webhooksEnabled: false,
    apiAccessEnabled: false,
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Delete dialog state
  const [deleteTenant, setDeleteTenant] = useState<Tenant | null>(null);
  const [deleteMfaCode, setDeleteMfaCode] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Expanded tenant users state
  const [tenantUsers, setTenantUsers] = useState<Record<string, any[]>>({});
  const [tenantUsersLoading, setTenantUsersLoading] = useState<string | null>(null);

  // Expanded tenant billing state
  const [tenantBilling, setTenantBilling] = useState<Record<string, any>>({});
  const [tenantBillingLoading, setTenantBillingLoading] = useState<string | null>(null);

  // Edit user dialog state
  const [editUser, setEditUser] = useState<any | null>(null);
  const [editUserForm, setEditUserForm] = useState({ email: '', displayName: '', role: '', isActive: true });
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [editUserError, setEditUserError] = useState<string | null>(null);

  // Reset password state
  const [resetPasswordLoading, setResetPasswordLoading] = useState<string | null>(null);
  const [resetPasswordMessage, setResetPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Suspend / Reactivate confirmation state
  const [confirmSuspend, setConfirmSuspend] = useState<Tenant | null>(null);
  const [confirmReactivate, setConfirmReactivate] = useState<Tenant | null>(null);

  // Hard delete state
  const [hardDeleteTenant, setHardDeleteTenant] = useState<Tenant | null>(null);
  const [hardDeleteMfaCode, setHardDeleteMfaCode] = useState('');
  const [hardDeleteLoading, setHardDeleteLoading] = useState(false);
  const [hardDeleteError, setHardDeleteError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [planFilter, setPlanFilter] = useState<string>('');

  // Search
  const [search, setSearch] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await platformApi.getTenants(statusFilter || undefined, planFilter || undefined);
      setTenants(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, planFilter]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const handleCreate = async () => {
    if (!newTenant.name.trim()) {
      setCreateError('Tenant name is required');
      return;
    }
    if (!newTenant.ownerEmail.trim() || !newTenant.ownerName.trim() || !newTenant.ownerPassword.trim()) {
      setCreateError('Owner name, email, and password are required');
      return;
    }
    if (newTenant.ownerPassword.length < 8) {
      setCreateError('Owner password must be at least 8 characters');
      return;
    }
    setCreateLoading(true);
    setCreateError(null);
    try {
      await platformApi.createTenant(newTenant);
      setShowCreate(false);
      setNewTenant({ name: '', plan: 'Starter', maxExecutiveSeats: 1, maxEaSeats: 1, ownerName: '', ownerEmail: '', ownerPassword: '' });
      loadTenants();
    } catch (err: any) {
      setCreateError(err.response?.data?.message || err.response?.data?.errors?.join(', ') || err.message || 'Failed to create tenant');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleOpenEdit = (tenant: Tenant) => {
    setEditTenant(tenant);
    setEditForm({
      name: tenant.name,
      plan: tenant.plan,
      maxExecutiveSeats: tenant.maxExecutiveSeats,
      maxEaSeats: tenant.maxEaSeats,
      billingEmail: tenant.billingEmail || '',
      status: tenant.status,
      aiPlanningEnabled: tenant.aiPlanningEnabled ?? true,
      transcriptProcessingEnabled: tenant.transcriptProcessingEnabled ?? false,
      imageAnalysisEnabled: tenant.imageAnalysisEnabled ?? true,
      pdfExportEnabled: tenant.pdfExportEnabled ?? false,
      webhooksEnabled: tenant.webhooksEnabled ?? false,
      apiAccessEnabled: tenant.apiAccessEnabled ?? false,
    });
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editTenant) return;
    setEditLoading(true);
    setEditError(null);
    try {
      await platformApi.updateTenant(editTenant.id, editForm);
      setEditTenant(null);
      loadTenants();
    } catch (err: any) {
      setEditError(err.response?.data?.message || err.message || 'Failed to update tenant');
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenDelete = (tenant: Tenant) => {
    setDeleteTenant(tenant);
    setDeleteMfaCode('');
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTenant) return;
    if (deleteMfaCode.length !== 6) {
      setDeleteError('Please enter a valid 6-digit MFA code');
      return;
    }
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await platformApi.deleteTenant(deleteTenant.id, deleteMfaCode);
      setDeleteTenant(null);
      loadTenants();
    } catch (err: any) {
      setDeleteError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to delete tenant');
    } finally {
      setDeleteLoading(false);
    }
  };

  const loadTenantUsers = useCallback(async (tenantId: string) => {
    setTenantUsersLoading(tenantId);
    try {
      const data = await platformApi.getTenant(tenantId);
      setTenantUsers(prev => ({ ...prev, [tenantId]: data.users || [] }));
    } catch {
      // Silently fail — users section will just be empty
    } finally {
      setTenantUsersLoading(null);
    }
  }, []);

  const loadTenantBilling = useCallback(async (tenantId: string) => {
    setTenantBillingLoading(tenantId);
    try {
      const data = await platformApi.getTenantBillingHistory(tenantId);
      setTenantBilling(prev => ({ ...prev, [tenantId]: data }));
    } catch {
      // Silently fail — billing section will just be empty
    } finally {
      setTenantBillingLoading(null);
    }
  }, []);

  const toggleExpand = (id: string) => {
    const isExpanding = expandedId !== id;
    setExpandedId(prev => prev === id ? null : id);
    if (isExpanding) {
      if (!tenantUsers[id]) loadTenantUsers(id);
      if (!tenantBilling[id]) loadTenantBilling(id);
    }
  };

  const handleOpenEditUser = (user: any) => {
    setEditUser(user);
    setEditUserForm({
      email: user.email || '',
      displayName: user.displayName || '',
      role: user.role || 'Executive',
      isActive: user.isActive ?? true,
    });
    setEditUserError(null);
  };

  const handleSaveEditUser = async () => {
    if (!editUser) return;
    setEditUserLoading(true);
    setEditUserError(null);
    try {
      await platformApi.updateUser(editUser.id, editUserForm);
      setEditUser(null);
      // Reload the tenant users for the expanded tenant
      if (expandedId) loadTenantUsers(expandedId);
    } catch (err: any) {
      setEditUserError(err.response?.data?.message || err.message || 'Failed to update user');
    } finally {
      setEditUserLoading(false);
    }
  };

  const handleResetPassword = async (userId: string) => {
    setResetPasswordLoading(userId);
    setResetPasswordMessage(null);
    try {
      const result = await platformApi.resetUserPassword(userId);
      setResetPasswordMessage({ type: 'success', text: result.message || 'Password reset initiated.' });
    } catch (err: any) {
      setResetPasswordMessage({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to reset password' });
    } finally {
      setResetPasswordLoading(null);
    }
  };

  const handleConfirmSuspend = async () => {
    if (!confirmSuspend) return;
    setActionLoading(confirmSuspend.id);
    try {
      await platformApi.suspendTenant(confirmSuspend.id);
      setConfirmSuspend(null);
      loadTenants();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to suspend tenant');
      setConfirmSuspend(null);
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmReactivate = async () => {
    if (!confirmReactivate) return;
    setActionLoading(confirmReactivate.id);
    try {
      await platformApi.reactivateTenant(confirmReactivate.id);
      setConfirmReactivate(null);
      loadTenants();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reactivate tenant');
      setConfirmReactivate(null);
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmHardDelete = async () => {
    if (!hardDeleteTenant) return;
    if (hardDeleteMfaCode.length !== 6) {
      setHardDeleteError('Please enter a valid 6-digit MFA code');
      return;
    }
    setHardDeleteLoading(true);
    setHardDeleteError(null);
    try {
      await platformApi.hardDeleteTenant(hardDeleteTenant.id, hardDeleteMfaCode);
      setHardDeleteTenant(null);
      setHardDeleteMfaCode('');
      loadTenants();
    } catch (err: any) {
      setHardDeleteError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to permanently delete tenant');
    } finally {
      setHardDeleteLoading(false);
    }
  };

  // Client-side search filter
  const filteredTenants = useMemo(() => {
    if (!search) return tenants;
    const s = search.toLowerCase();
    return tenants.filter(t =>
      t.name?.toLowerCase().includes(s) ||
      t.domain?.toLowerCase().includes(s) ||
      t.billingEmail?.toLowerCase().includes(s)
    );
  }, [tenants, search]);

  // Reset page when search/filter changes
  useEffect(() => { setPage(0); }, [search, statusFilter, planFilter]);

  // Health dashboard counts
  const healthCounts = useMemo(() => {
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    let healthy = 0;
    let atRisk = 0;
    let critical = 0;

    tenants.forEach(t => {
      const status = t.status;
      if (status === 'Suspended' || status === 'Cancelled' || status === 'Deleted') {
        critical++;
      } else if (status === 'Trial' && t.trialExpiresAt && new Date(t.trialExpiresAt) <= now) {
        critical++;
      } else if (
        (status === 'Trial' && t.trialExpiresAt && new Date(t.trialExpiresAt) <= sevenDaysFromNow) ||
        status === 'PastDue'
      ) {
        atRisk++;
      } else {
        healthy++;
      }
    });

    return { healthy, atRisk, critical };
  }, [tenants]);

  // CSV export
  const handleExport = () => {
    const headers = 'Name,Plan,Status,Executives,EAs,Billing Email,Created\n';
    const rows = filteredTenants.map(t =>
      `"${t.name}","${t.plan}","${t.status}",${getExecCount(t)},${getEaCount(t)},"${t.billingEmail || ''}","${t.createdAt}"`
    ).join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cadence-tenants-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Paginated tenants
  const paginatedTenants = filteredTenants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TenantsIcon sx={{ color: 'error.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h4">Tenants</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage platform tenants and subscriptions
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="error"
          startIcon={<AddIcon />}
          onClick={() => setShowCreate(true)}
          sx={{ fontWeight: 600 }}
        >
          Create Tenant
        </Button>
      </Box>

      {loading && <LinearProgress color="error" sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {resetPasswordMessage && (
        <Alert severity={resetPasswordMessage.type} sx={{ mb: 2 }} onClose={() => setResetPasswordMessage(null)}>
          {resetPasswordMessage.text}
        </Alert>
      )}

      {/* Tenant Health Dashboard */}
      {!loading && tenants.length > 0 && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
          <Chip
            label={`Healthy: ${healthCounts.healthy}`}
            size="small"
            sx={{
              bgcolor: 'rgba(76, 175, 80, 0.12)',
              color: 'success.main',
              fontWeight: 600,
              fontSize: '0.8rem',
              '& .MuiChip-label': { px: 1.5 },
            }}
            icon={<Box component="span" sx={{ color: 'success.main', fontSize: '0.7rem', ml: 1 }}>&#9679;</Box>}
          />
          <Chip
            label={`At Risk: ${healthCounts.atRisk}`}
            size="small"
            sx={{
              bgcolor: 'rgba(255, 152, 0, 0.12)',
              color: 'warning.main',
              fontWeight: 600,
              fontSize: '0.8rem',
              '& .MuiChip-label': { px: 1.5 },
            }}
            icon={<Box component="span" sx={{ color: 'warning.main', fontSize: '0.7rem', ml: 1 }}>&#9679;</Box>}
          />
          <Chip
            label={`Critical: ${healthCounts.critical}`}
            size="small"
            sx={{
              bgcolor: 'rgba(244, 67, 54, 0.12)',
              color: 'error.main',
              fontWeight: 600,
              fontSize: '0.8rem',
              '& .MuiChip-label': { px: 1.5 },
            }}
            icon={<Box component="span" sx={{ color: 'error.main', fontSize: '0.7rem', ml: 1 }}>&#9679;</Box>}
          />
        </Stack>
      )}

      {/* Search Bar */}
      <TextField
        size="small"
        placeholder="Search tenants by name, domain, or billing email..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2, width: '100%', maxWidth: 400 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Filters + Export */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Trial">Trial</MenuItem>
          <MenuItem value="Suspended">Suspended</MenuItem>
          <MenuItem value="Cancelled">Cancelled</MenuItem>
          <MenuItem value="Deleted">Deleted</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Plan"
          value={planFilter}
          onChange={e => setPlanFilter(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Trial">Trial</MenuItem>
          <MenuItem value="Starter">Starter</MenuItem>
          <MenuItem value="Professional">Professional</MenuItem>
          <MenuItem value="Enterprise">Enterprise</MenuItem>
        </TextField>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={filteredTenants.length === 0}
          sx={{ fontWeight: 600 }}
        >
          Export CSV
        </Button>
      </Stack>

      {/* Tenant Table */}
      <Card sx={{ bgcolor: 'background.paper' }}>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, width: 40 }} />
                  <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Plan</TableCell>
                  <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Executives</TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>EAs</TableCell>
                  <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Created</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTenants.map(tenant => (
                  <React.Fragment key={tenant.id}>
                    <TableRow
                      hover
                      sx={{ cursor: 'pointer', '& td': { borderColor: 'divider' } }}
                      onClick={() => toggleExpand(tenant.id)}
                    >
                      <TableCell sx={{ width: 40, pr: 0 }}>
                        <IconButton size="small">
                          {expandedId === tenant.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>
                        {tenant.name}
                      </TableCell>
                      <TableCell>
                        <Chip label={tenant.plan} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tenant.status}
                          size="small"
                          color={STATUS_CONFIG[tenant.status]?.color || 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                          {getExecCount(tenant)} / {tenant.maxExecutiveSeats}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                          {getEaCount(tenant)} / {tenant.maxEaSeats}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {new Date(tenant.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={e => e.stopPropagation()}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Edit tenant">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenEdit(tenant)}
                              disabled={actionLoading === tenant.id}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {tenant.status === 'Active' && (
                            <Tooltip title="Suspend tenant">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => setConfirmSuspend(tenant)}
                                disabled={actionLoading === tenant.id}
                              >
                                <SuspendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {tenant.status === 'Suspended' && (
                            <Tooltip title="Reactivate tenant">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => setConfirmReactivate(tenant)}
                                disabled={actionLoading === tenant.id}
                              >
                                <ReactivateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {tenant.status !== 'Deleted' && (
                            <Tooltip title="Delete tenant">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenDelete(tenant)}
                                disabled={actionLoading === tenant.id}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {tenant.status === 'Deleted' && (
                            <Tooltip title="Permanently delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setHardDeleteTenant(tenant)}
                                disabled={actionLoading === tenant.id}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, borderColor: 'divider' }}>
                        <Collapse in={expandedId === tenant.id} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 2 }}>
                            <Stack direction="row" spacing={4} flexWrap="wrap" sx={{ mb: 2 }}>
                              <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Tenant ID</Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                  {tenant.id}
                                </Typography>
                              </Box>
                              {(tenant.stripeCustomerId || tenantBilling[tenant.id]?.billing?.stripeCustomerId) && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Stripe Customer ID</Typography>
                                  <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                    {tenant.stripeCustomerId || tenantBilling[tenant.id]?.billing?.stripeCustomerId}
                                  </Typography>
                                </Box>
                              )}
                              {(tenant.stripeSubscriptionId || tenantBilling[tenant.id]?.billing?.stripeSubscriptionId) && (
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Stripe Subscription ID</Typography>
                                  <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                    {tenant.stripeSubscriptionId || tenantBilling[tenant.id]?.billing?.stripeSubscriptionId}
                                  </Typography>
                                </Box>
                              )}
                              <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Executive Seats</Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                  {getExecCount(tenant)} used of {tenant.maxExecutiveSeats}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>EA Seats</Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                  {getEaCount(tenant)} used of {tenant.maxEaSeats}
                                </Typography>
                              </Box>
                            </Stack>

                            {/* Subscription History */}
                            {tenantBillingLoading === tenant.id ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={20} />
                              </Box>
                            ) : tenantBilling[tenant.id]?.events && tenantBilling[tenant.id].events.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, mt: 2 }}>
                                  Subscription History
                                </Typography>
                                <TableContainer>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Action</TableCell>
                                        <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>From</TableCell>
                                        <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>To</TableCell>
                                        <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Date</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {tenantBilling[tenant.id].events.map((evt: any) => (
                                        <TableRow key={evt.id}>
                                          <TableCell sx={{ color: 'text.primary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                            <Chip label={evt.action} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                          </TableCell>
                                          <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                            {evt.fromValue || '-'}
                                          </TableCell>
                                          <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                            {evt.toValue || '-'}
                                          </TableCell>
                                          <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                            {new Date(evt.timestamp).toLocaleDateString()}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}

                            {/* Tenant Users */}
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, mt: 2 }}>
                              Users
                            </Typography>
                            {tenantUsersLoading === tenant.id ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : tenantUsers[tenant.id] && tenantUsers[tenant.id].length > 0 ? (
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Name</TableCell>
                                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Email</TableCell>
                                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Role</TableCell>
                                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Last Login</TableCell>
                                      <TableCell align="right" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {tenantUsers[tenant.id].map((u: any) => (
                                      <TableRow key={u.id}>
                                        <TableCell sx={{ color: 'text.primary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                          {u.displayName || '---'}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.primary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                          {u.email}
                                        </TableCell>
                                        <TableCell sx={{ borderColor: 'divider' }}>
                                          <Chip label={u.role} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                        </TableCell>
                                        <TableCell sx={{ borderColor: 'divider' }}>
                                          <Chip
                                            label={u.isActive ? 'Active' : 'Inactive'}
                                            size="small"
                                            color={u.isActive ? 'success' : 'default'}
                                            variant="outlined"
                                            sx={{ fontSize: '0.7rem' }}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                          {u.lastLoginAt
                                            ? new Date(u.lastLoginAt).toLocaleDateString()
                                            : 'Never'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ borderColor: 'divider' }}>
                                          <Tooltip title="Edit user">
                                            <IconButton size="small" color="primary" onClick={() => handleOpenEditUser(u)}>
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Reset password">
                                            <IconButton
                                              size="small"
                                              color="warning"
                                              onClick={() => handleResetPassword(u.id)}
                                              disabled={resetPasswordLoading === u.id}
                                            >
                                              {resetPasswordLoading === u.id
                                                ? <CircularProgress size={16} />
                                                : <LockResetIcon fontSize="small" />
                                              }
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                                No users found in this tenant.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                {!loading && paginatedTenants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        {search ? 'No tenants match your search' : 'No tenants found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredTenants.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      {/* Create Tenant Dialog */}
      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Tenant</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 1 }}>
              Organisation
            </Typography>
            <TextField
              label="Tenant Name"
              fullWidth
              value={newTenant.name}
              onChange={e => setNewTenant(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Acme Corporation"
            />
            <TextField
              label="Plan"
              select
              fullWidth
              value={newTenant.plan}
              onChange={e => setNewTenant(prev => ({ ...prev, plan: e.target.value }))}
            >
              <MenuItem value="Trial">Trial</MenuItem>
              <MenuItem value="Starter">Starter</MenuItem>
              <MenuItem value="Professional">Professional</MenuItem>
              <MenuItem value="Enterprise">Enterprise</MenuItem>
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Executive Seats"
                type="number"
                fullWidth
                value={newTenant.maxExecutiveSeats}
                onChange={e => setNewTenant(prev => ({ ...prev, maxExecutiveSeats: parseInt(e.target.value) || 1 }))}
                inputProps={{ min: 1 }}
              />
              <TextField
                label="EA Seats"
                type="number"
                fullWidth
                value={newTenant.maxEaSeats}
                onChange={e => setNewTenant(prev => ({ ...prev, maxEaSeats: parseInt(e.target.value) || 1 }))}
                inputProps={{ min: 1 }}
              />
            </Stack>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 2 }}>
              Owner (Executive User)
            </Typography>
            <TextField
              label="Owner Name"
              fullWidth
              required
              value={newTenant.ownerName}
              onChange={e => setNewTenant(prev => ({ ...prev, ownerName: e.target.value }))}
              placeholder="e.g. Jane Smith"
            />
            <TextField
              label="Owner Email"
              fullWidth
              required
              type="email"
              value={newTenant.ownerEmail}
              onChange={e => setNewTenant(prev => ({ ...prev, ownerEmail: e.target.value }))}
              placeholder="e.g. jane@acme.com"
            />
            <TextField
              label="Owner Password"
              fullWidth
              required
              type="password"
              value={newTenant.ownerPassword}
              onChange={e => setNewTenant(prev => ({ ...prev, ownerPassword: e.target.value }))}
              helperText="Minimum 8 characters"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleCreate} disabled={createLoading}>
            {createLoading ? 'Creating...' : 'Create Tenant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={!!editTenant} onClose={() => setEditTenant(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Tenant: {editTenant?.name}</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              value={editForm.name}
              onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              label="Plan"
              select
              fullWidth
              value={editForm.plan}
              onChange={e => setEditForm(prev => ({ ...prev, plan: e.target.value }))}
            >
              <MenuItem value="Trial">Trial</MenuItem>
              <MenuItem value="Starter">Starter</MenuItem>
              <MenuItem value="Professional">Professional</MenuItem>
              <MenuItem value="Enterprise">Enterprise</MenuItem>
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Max Executive Seats"
                type="number"
                fullWidth
                value={editForm.maxExecutiveSeats}
                onChange={e => setEditForm(prev => ({ ...prev, maxExecutiveSeats: parseInt(e.target.value) || 1 }))}
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Max EA Seats"
                type="number"
                fullWidth
                value={editForm.maxEaSeats}
                onChange={e => setEditForm(prev => ({ ...prev, maxEaSeats: parseInt(e.target.value) || 1 }))}
                inputProps={{ min: 1 }}
              />
            </Stack>
            <TextField
              label="Billing Email"
              fullWidth
              type="email"
              value={editForm.billingEmail}
              onChange={e => setEditForm(prev => ({ ...prev, billingEmail: e.target.value }))}
            />
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 2 }}>
              Feature Toggles
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.aiPlanningEnabled}
                    onChange={e => setEditForm(prev => ({ ...prev, aiPlanningEnabled: e.target.checked }))}
                    size="small"
                  />
                }
                label="AI Planning"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.transcriptProcessingEnabled}
                    onChange={e => setEditForm(prev => ({ ...prev, transcriptProcessingEnabled: e.target.checked }))}
                    size="small"
                  />
                }
                label="Transcripts"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.imageAnalysisEnabled}
                    onChange={e => setEditForm(prev => ({ ...prev, imageAnalysisEnabled: e.target.checked }))}
                    size="small"
                  />
                }
                label="Image Analysis"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.pdfExportEnabled}
                    onChange={e => setEditForm(prev => ({ ...prev, pdfExportEnabled: e.target.checked }))}
                    size="small"
                  />
                }
                label="PDF Export"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.webhooksEnabled}
                    onChange={e => setEditForm(prev => ({ ...prev, webhooksEnabled: e.target.checked }))}
                    size="small"
                  />
                }
                label="Webhooks"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.apiAccessEnabled}
                    onChange={e => setEditForm(prev => ({ ...prev, apiAccessEnabled: e.target.checked }))}
                    size="small"
                  />
                }
                label="API Access"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTenant(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleSaveEdit} disabled={editLoading}>
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User: {editUser?.displayName || editUser?.email}</DialogTitle>
        <DialogContent>
          {editUserError && <Alert severity="error" sx={{ mb: 2 }}>{editUserError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              fullWidth
              type="email"
              value={editUserForm.email}
              onChange={e => setEditUserForm(prev => ({ ...prev, email: e.target.value }))}
            />
            <TextField
              label="Display Name"
              fullWidth
              value={editUserForm.displayName}
              onChange={e => setEditUserForm(prev => ({ ...prev, displayName: e.target.value }))}
            />
            <TextField
              label="Role"
              select
              fullWidth
              value={editUserForm.role}
              onChange={e => setEditUserForm(prev => ({ ...prev, role: e.target.value }))}
            >
              <MenuItem value="Executive">Executive</MenuItem>
              <MenuItem value="ExecutiveAssistant">Executive Assistant</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="TenantAdmin">Tenant Admin</MenuItem>
              <MenuItem value="PlatformAdmin">Platform Admin</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={editUserForm.isActive}
                  onChange={e => setEditUserForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleSaveEditUser} disabled={editUserLoading}>
            {editUserLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Tenant Dialog */}
      <Dialog open={!!deleteTenant} onClose={() => setDeleteTenant(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Tenant</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will permanently deactivate the tenant <strong>{deleteTenant?.name}</strong> and all its users. This action requires MFA verification.
          </Alert>
          <TextField
            label="MFA Code"
            fullWidth
            value={deleteMfaCode}
            onChange={e => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setDeleteMfaCode(val);
            }}
            placeholder="Enter 6-digit MFA code"
            helperText="Enter the 6-digit code from your authenticator app"
            inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTenant(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteLoading || deleteMfaCode.length !== 6}
          >
            {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Confirmation Dialog */}
      <Dialog open={!!confirmSuspend} onClose={() => setConfirmSuspend(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Suspend Tenant?</DialogTitle>
        <DialogContent>
          <Typography>
            This will block all API access for <strong>{confirmSuspend?.name}</strong>. Their users will not be able to use Cadence until reactivated.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSuspend(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmSuspend}>
            Suspend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reactivate Confirmation Dialog */}
      <Dialog open={!!confirmReactivate} onClose={() => setConfirmReactivate(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reactivate Tenant?</DialogTitle>
        <DialogContent>
          <Typography>
            This will restore API access for <strong>{confirmReactivate?.name}</strong>. Their users will be able to log in and use Cadence again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReactivate(null)}>Cancel</Button>
          <Button color="success" variant="contained" onClick={handleConfirmReactivate}>
            Reactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hard Delete Confirmation Dialog */}
      <Dialog open={!!hardDeleteTenant} onClose={() => setHardDeleteTenant(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Permanently Delete Tenant</DialogTitle>
        <DialogContent>
          {hardDeleteError && <Alert severity="error" sx={{ mb: 2 }}>{hardDeleteError}</Alert>}
          <Alert severity="error" sx={{ mb: 2 }}>
            This will <strong>permanently delete all data</strong> for <strong>{hardDeleteTenant?.name}</strong> including users, work items, plans, and calendar data. This action is <strong>irreversible</strong> and requires MFA verification.
          </Alert>
          <TextField
            label="MFA Code"
            fullWidth
            value={hardDeleteMfaCode}
            onChange={e => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setHardDeleteMfaCode(val);
            }}
            placeholder="Enter 6-digit MFA code"
            helperText="Enter the 6-digit code from your authenticator app"
            inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setHardDeleteTenant(null); setHardDeleteMfaCode(''); setHardDeleteError(null); }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmHardDelete}
            disabled={hardDeleteLoading || hardDeleteMfaCode.length !== 6}
          >
            {hardDeleteLoading ? 'Deleting...' : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
